import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { useUser, useUserContext } from '../../../../context/UserProvider'
import FleetReportTable from '../components/FleetReportTable'
import { ConsolidateInvoiceSelect } from '../../../cars/components/V3/_shared/ConsolidateInvoiceSelect'
import type { Fleet } from '../../../../types'
import { ExcelExportButton } from '../components/ExcelExporter'
import { fleetReportColumns } from '../components/FleetReportTable/excelUtils'
import { fetchFleetReport, fetchReportTermLimits } from '../../../../api/newApi'
import ImprovedDatePicker from '../../../../components/fields/ImprovedDatePicker'
import { dateToValue, monthInputValue, valueToDate } from '../../../../utils/helpers/helpers.reports'
import { Input } from '../../../cars/components/V3/_shared/Form/Input'
import { SearchIcon } from '../../../cars/components/V3/SearchIcon'

const clampMonth = (value: string, min: string, max: string) => {
  if (value < min) return min
  if (value > max) return max
  return value
}

const monthToTerm = (month: string) => `${month}-01`

const FleetReport = () => {
  const user = useUser()
  const { chosenCompany: company, fleetReportTerm, consolidateInvoice } = useUserContext() as any
  const companyId = company?.id
  const contactId = user?.user?.id
  const [term, setTerm] = useState(fleetReportTerm || '')
  const [search, setSearch] = useState('')
  const consolidateCode = consolidateInvoice?.ConsolidateCode || '_ALL_'

  const { data: termLimits } = useQuery(
    ['fleetReportTermLimits', companyId, contactId],
    () => fetchReportTermLimits({ companyId, contactId, type: 'fleet' }),
    {
      enabled: !!companyId && !!contactId,
    },
  ) as { data?: { oldest: string; newest: string } }

  const oldestMonth = termLimits?.oldest ? monthInputValue(termLimits.oldest) : undefined
  const newestMonth = termLimits?.newest ? monthInputValue(termLimits.newest) : undefined

  useEffect(() => {
    if (!oldestMonth || !newestMonth) return

    const currentMonth = term ? monthInputValue(term) : newestMonth
    const nextTerm = monthToTerm(clampMonth(currentMonth, oldestMonth, newestMonth))

    if (nextTerm !== term) {
      setTerm(nextTerm)
      return
    }

    localStorage.setItem('fleetReportTerm', nextTerm)
  }, [newestMonth, oldestMonth, term])

  const { data: response, status } = useQuery(
    ['fleetReport', companyId, term, contactId, 'fleet', consolidateCode],
    () =>
      fetchFleetReport({
        term,
        companyId,
        contactId,
        consolidatedAgreementID: consolidateCode,
      }),
    { enabled: !!companyId && !!contactId && !!term },
  )

  const items = (response && 'items' in response ? response.items : []) as Fleet[]
  const filteredItems = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()
    if (!normalizedSearch) return items

    return items.filter((item) => {
      const status = item.accountStatus === 'AVVIKLET' || item.accountStatus === 'ADM' ? 'ADM' : item.termsNow
      const searchableFields = [item.regNo, item.driverName, status, item.totalTerms]

      return searchableFields.some((value) =>
        String(value || '')
          .toLowerCase()
          .includes(normalizedSearch),
      )
    })
  }, [items, search])

  const excelData = filteredItems.map((row) => ({
    ...row,
    HK: row.accountStatus === 'AVVIKLET' || row.accountStatus === 'ADM' ? 'ADM' : String(row.termsNow || ''),
  }))

  const config = {
    columns: fleetReportColumns(),
    filename: `Fleetrapport_${term}.xlsx`,
    sheetName: `Fleetrapport_${term}`,
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex flex-row justify-between items-center">
          <span className="text-xl font-medium text-primaryText">Flåterapport</span>
          <div className="md:hidden">
            <ExcelExportButton data={excelData} config={config} />
          </div>
        </div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 w-full">
          <div className="relative w-full md:w-80">
            <Input
              id="fleet-report-search"
              type="search"
              value={search}
              placeholder="Søk"
              className="h-11 pl-11"
              onChange={(event) => setSearch(event.target.value)}
            />
            <div className="absolute top-[14px] left-[14px]">
              <SearchIcon />
            </div>
          </div>
          <div className="flex flex-row items-end gap-2 w-full md:w-auto">
            <div className="flex flex-col gap-1 text-sm text-secondaryText flex-1 md:flex-none">
              <label htmlFor="fleet-report-term">Termin</label>
              <ImprovedDatePicker
                id="fleet-report-term"
                selected={valueToDate(term ? monthInputValue(term) : newestMonth || '', 'month')}
                language="nb"
                type="month"
                minDate={oldestMonth ? valueToDate(oldestMonth, 'month') : undefined}
                maxDate={newestMonth ? valueToDate(newestMonth, 'month') : undefined}
                disabled={!oldestMonth || !newestMonth}
                onChange={(date) => date && setTerm(monthToTerm(dateToValue(date, 'month')))}
              />
            </div>
            <div className="flex-1 md:flex-none self-end">
              <ConsolidateInvoiceSelect className="h-11 w-full" />
            </div>
            <div className="hidden md:block self-end">
              <ExcelExportButton className="h-11" data={excelData} config={config} />
            </div>
          </div>
        </div>
      </div>

      <FleetReportTable isLoading={status === 'loading'} data={filteredItems} />
    </div>
  )
}

export default FleetReport
